import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { FadeUp } from "@/components/Motion";
import { toast } from "sonner";
import { Plus, Copy, Power, PowerOff } from "lucide-react";

export default function Admin() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [codes, setCodes] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("stats");

  // Discount code form
  const [dcCode, setDcCode] = useState("");
  const [dcKind, setDcKind] = useState("percent");
  const [dcValue, setDcValue] = useState(10);
  const [dcExpires, setDcExpires] = useState("");
  const [dcMax, setDcMax] = useState("");
  const [dcDesc, setDcDesc] = useState("");

  const loadCodes = () => api.get("/admin/discount-codes").then((r) => setCodes(r.data));
  const loadGifts = () => api.get("/admin/gift-cards").then((r) => setGifts(r.data));
  const loadReviews = () => api.get("/admin/reviews").then((r) => setReviews(r.data));

  useEffect(() => {
    if (user?.is_admin) {
      api.get("/admin/stats").then((r) => setStats(r.data));
      api.get("/admin/orders").then((r) => setOrders(r.data));
      api.get("/admin/contacts").then((r) => setContacts(r.data));
      api.get("/admin/newsletter").then((r) => setSubs(r.data));
      loadCodes();
      loadGifts();
      loadReviews();
    }
  }, [user]);

  if (loading) return <div className="pt-40 text-center text-cream/50">Loading...</div>;
  if (!user || !user.is_admin) return <Navigate to="/login" />;

  const createCode = async (e) => {
    e.preventDefault();
    if (!dcCode.trim() || !dcValue) { toast.error("Code + Wert nötig"); return; }
    try {
      await api.post("/admin/discount-codes", {
        code: dcCode.trim().toUpperCase(),
        kind: dcKind,
        value: Number(dcValue),
        expires_at: dcExpires || undefined,
        max_uses: dcMax ? Number(dcMax) : undefined,
        description: dcDesc,
      });
      toast.success("Code angelegt");
      setDcCode(""); setDcDesc(""); setDcExpires(""); setDcMax(""); setDcValue(10);
      loadCodes();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler");
    }
  };

  const toggleCode = async (code, active) => {
    await api.patch(`/admin/discount-codes/${code}`, { active: !active });
    loadCodes();
  };

  const toggleGift = async (code, status) => {
    const next = status === "active" ? "disabled" : "active";
    await api.patch(`/admin/gift-cards/${code}`, { status: next });
    loadGifts();
  };

  const toggleReview = async (id, approved) => {
    await api.patch(`/admin/reviews/${id}`, { approved: !approved });
    loadReviews();
  };

  const deleteReview = async (id) => {
    await api.delete(`/admin/reviews/${id}`);
    loadReviews();
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Kopiert: " + text); };

  const tabs = [
    { k: "stats", label: t.admin.stats },
    { k: "orders", label: t.admin.orders },
    { k: "codes", label: "Rabattcodes" },
    { k: "gifts", label: "Gutscheine" },
    { k: "reviews", label: "Bewertungen" },
    { k: "contacts", label: t.admin.contacts },
    { k: "subs", label: t.admin.subs },
  ];

  return (
    <div className="pt-32 pb-32 min-h-screen" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <FadeUp className="mb-12">
          <div className="font-display text-[10px] tracking-[0.4em] text-gold mb-4">/ {t.admin.t}</div>
          <h1 className="font-editorial italic font-light text-5xl text-cream">Dashboard</h1>
        </FadeUp>

        <div className="flex gap-8 border-b border-white/10 mb-12">
          {tabs.map((tt) => (
            <button
              key={tt.k}
              onClick={() => setTab(tt.k)}
              data-testid={`admin-tab-${tt.k}`}
              className={`font-display text-[10px] tracking-[0.3em] py-4 border-b-2 transition-colors -mb-px ${
                tab === tt.k ? "border-gold text-gold" : "border-transparent text-cream/60 hover:text-cream"
              }`}
            >
              {tt.label}
            </button>
          ))}
        </div>

        {tab === "stats" && stats && (
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            {[
              { k: "Users", v: stats.users },
              { k: "Orders", v: stats.orders },
              { k: "Paid", v: stats.paid_orders },
              { k: "Subscribers", v: stats.newsletter },
              { k: "Contacts", v: stats.contacts },
            ].map((s, i) => (
              <div key={i} className="border border-white/10 p-8 clip-frame" data-testid={`stat-${s.k}`}>
                <div className="font-editorial italic text-5xl text-gold mb-3">{s.v}</div>
                <div className="font-display text-[10px] tracking-[0.3em] text-cream/60">{s.k}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="admin-orders-list">
            {orders.length === 0 && <p className="text-cream/50">No orders</p>}
            {orders.map((o) => (
              <div key={o.session_id} className="border border-white/10 p-6 grid md:grid-cols-4 gap-4 items-center">
                <div className="text-xs text-cream/60 font-body">{new Date(o.created_at).toLocaleString()}</div>
                <div className="text-sm text-cream">{o.email || "guest"}</div>
                <div className="text-sm text-cream/70 font-body">{(o.items || []).map((i) => `${i.name} ×${i.qty}`).join(", ")}</div>
                <div className="text-right">
                  <div className="font-editorial text-xl text-gold">€{((o.amount || 0) / 100).toFixed(2)}</div>
                  <div className={`font-display text-[9px] tracking-[0.3em] ${o.payment_status === "paid" ? "text-gold" : "text-cream/50"}`}>{o.payment_status}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "codes" && (
          <div className="space-y-8" data-testid="admin-codes-panel">
            {/* Create form */}
            <form onSubmit={createCode} className="border border-white/10 p-6 bg-[#0d0d0d] grid md:grid-cols-6 gap-4 items-end">
              <div className="md:col-span-2">
                <label className="font-display text-[9px] tracking-[0.3em] text-cream/60 block mb-2">CODE</label>
                <input value={dcCode} onChange={(e) => setDcCode(e.target.value.toUpperCase())} placeholder="Z.B. WEIHNACHTEN10" data-testid="dc-code" className="bg-transparent border-b border-white/15 focus:border-gold py-2 outline-none w-full text-cream" />
              </div>
              <div>
                <label className="font-display text-[9px] tracking-[0.3em] text-cream/60 block mb-2">TYP</label>
                <select value={dcKind} onChange={(e) => setDcKind(e.target.value)} data-testid="dc-kind" className="bg-black border border-white/15 focus:border-gold py-2 px-2 outline-none w-full text-cream text-sm">
                  <option value="percent">Prozent (%)</option>
                  <option value="fixed">Fest (€)</option>
                </select>
              </div>
              <div>
                <label className="font-display text-[9px] tracking-[0.3em] text-cream/60 block mb-2">WERT</label>
                <input type="number" step="0.01" value={dcValue} onChange={(e) => setDcValue(e.target.value)} data-testid="dc-value" className="bg-transparent border-b border-white/15 focus:border-gold py-2 outline-none w-full text-cream" />
              </div>
              <div>
                <label className="font-display text-[9px] tracking-[0.3em] text-cream/60 block mb-2">GÜLTIG BIS</label>
                <input type="date" value={dcExpires ? dcExpires.slice(0, 10) : ""} onChange={(e) => setDcExpires(e.target.value ? new Date(e.target.value + "T23:59:59Z").toISOString() : "")} data-testid="dc-expires" className="bg-transparent border-b border-white/15 focus:border-gold py-2 outline-none w-full text-cream text-sm" />
              </div>
              <div>
                <label className="font-display text-[9px] tracking-[0.3em] text-cream/60 block mb-2">MAX. NUTZUNG</label>
                <input type="number" min="1" value={dcMax} onChange={(e) => setDcMax(e.target.value)} placeholder="∞" data-testid="dc-max" className="bg-transparent border-b border-white/15 focus:border-gold py-2 outline-none w-full text-cream" />
              </div>
              <div className="md:col-span-5">
                <label className="font-display text-[9px] tracking-[0.3em] text-cream/60 block mb-2">BESCHREIBUNG (nur intern)</label>
                <input value={dcDesc} onChange={(e) => setDcDesc(e.target.value)} placeholder="z.B. Weihnachtsaktion 2026" data-testid="dc-desc" className="bg-transparent border-b border-white/15 focus:border-gold py-2 outline-none w-full text-cream" />
              </div>
              <button type="submit" data-testid="dc-create" className="bg-gold text-black font-display text-[10px] tracking-[0.3em] py-3 px-4 hover:bg-gold-light inline-flex items-center justify-center gap-2" style={{ background: "#C9A24C" }}>
                <Plus className="w-3 h-3" /> ANLEGEN
              </button>
            </form>

            {/* List */}
            <div className="space-y-2">
              {codes.length === 0 && <p className="text-cream/50 text-sm">Noch keine Rabattcodes.</p>}
              {codes.map((c) => (
                <div key={c.code} data-testid={`dc-row-${c.code}`} className={`border border-white/10 p-4 grid md:grid-cols-6 gap-4 items-center ${!c.active ? "opacity-50" : ""}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-[11px] tracking-[0.2em] text-gold">{c.code}</span>
                    <button onClick={() => copy(c.code)} title="Kopieren" className="text-cream/40 hover:text-gold"><Copy className="w-3 h-3" /></button>
                  </div>
                  <div className="text-cream/70 text-sm">
                    {c.kind === "percent" ? `${c.value}%` : `€${Number(c.value).toFixed(2)}`}
                  </div>
                  <div className="text-cream/50 text-xs font-body">
                    {c.expires_at ? `bis ${new Date(c.expires_at).toLocaleDateString()}` : "unbegrenzt"}
                  </div>
                  <div className="text-cream/50 text-xs">
                    {c.uses}/{c.max_uses ?? "∞"} genutzt
                  </div>
                  <div className="text-cream/40 text-xs font-body truncate">{c.description}</div>
                  <div className="text-right">
                    <button onClick={() => toggleCode(c.code, c.active)} data-testid={`dc-toggle-${c.code}`} className={`inline-flex items-center gap-2 text-[10px] font-display tracking-[0.2em] px-3 py-1.5 border ${c.active ? "border-gold/40 text-gold hover:bg-gold hover:text-black" : "border-white/20 text-cream/60 hover:border-gold hover:text-gold"} transition-colors`}>
                      {c.active ? <><Power className="w-3 h-3" /> AKTIV</> : <><PowerOff className="w-3 h-3" /> INAKTIV</>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "gifts" && (
          <div className="space-y-2" data-testid="admin-gifts-panel">
            {gifts.length === 0 && <p className="text-cream/50 text-sm">Noch keine Gutscheine verkauft.</p>}
            {gifts.map((g) => (
              <div key={g.code} data-testid={`gc-row-${g.code}`} className="border border-white/10 p-5 grid md:grid-cols-6 gap-4 items-center">
                <div className="flex items-center gap-2 md:col-span-2">
                  <span className="font-display text-[11px] tracking-[0.15em] text-gold">{g.code}</span>
                  <button onClick={() => copy(g.code)} title="Kopieren" className="text-cream/40 hover:text-gold"><Copy className="w-3 h-3" /></button>
                </div>
                <div>
                  <div className="font-editorial text-lg text-gold">€{Number(g.amount_eur).toFixed(2)}</div>
                  <div className="text-[10px] text-cream/50 font-display tracking-[0.2em]">
                    RESTGUTHABEN €{Number(g.remaining_eur).toFixed(2)}
                  </div>
                </div>
                <div className="text-cream/70 text-xs font-body">
                  <div>{g.recipient_name || "—"}</div>
                  <div className="text-cream/40">{g.recipient_email || "—"}</div>
                </div>
                <div className="text-cream/40 text-xs font-body">
                  {new Date(g.created_at).toLocaleDateString()}
                </div>
                <div className="text-right">
                  <button onClick={() => toggleGift(g.code, g.status)} className={`inline-flex items-center gap-2 text-[10px] font-display tracking-[0.2em] px-3 py-1.5 border ${g.status === "active" ? "border-gold/40 text-gold hover:bg-gold hover:text-black" : "border-white/20 text-cream/60 hover:border-gold hover:text-gold"} transition-colors`}>
                    {g.status === "active" ? "AKTIV" : "GESPERRT"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "reviews" && (
          <div className="space-y-3" data-testid="admin-reviews-list">
            <p className="text-cream/40 text-sm mb-6">Link für Kunden: <span className="text-gold">https://www.madubau.com/bewertung</span> — freigegebene Bewertungen erscheinen automatisch im Laufband auf der Website.</p>
            {reviews.length === 0 && <p className="text-cream/50">Noch keine Bewertungen eingegangen.</p>}
            {reviews.map((r) => (
              <div key={r.id} className={`border p-6 flex flex-wrap items-center gap-4 ${r.approved ? "border-gold/30" : "border-white/10"}`} data-testid={`admin-review-${r.id}`}>
                <div className="flex gap-0.5 shrink-0">
                  {[...Array(5)].map((_, s) => (
                    <span key={s} className={`text-sm ${s < r.rating ? "text-gold" : "text-cream/20"}`}>★</span>
                  ))}
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-cream font-body text-sm">{r.text}</div>
                  <div className="text-cream/40 text-xs mt-1">— {r.name} · {r.lang?.toUpperCase()} · {new Date(r.created_at).toLocaleDateString("de-IT")}</div>
                </div>
                <button
                  onClick={() => toggleReview(r.id, r.approved)}
                  data-testid={`review-toggle-${r.id}`}
                  className={`font-display text-[9px] tracking-[0.25em] px-4 py-2 border transition-colors ${
                    r.approved ? "border-gold text-gold hover:bg-gold hover:text-black" : "border-white/20 text-cream/60 hover:border-gold hover:text-gold"
                  }`}
                >
                  {r.approved ? "SICHTBAR" : "FREIGEBEN"}
                </button>
                <button
                  onClick={() => deleteReview(r.id)}
                  data-testid={`review-delete-${r.id}`}
                  className="font-display text-[9px] tracking-[0.25em] px-4 py-2 border border-white/10 text-cream/40 hover:border-red-500/60 hover:text-red-400 transition-colors"
                >
                  LÖSCHEN
                </button>
              </div>
            ))}
          </div>
        )}


        {tab === "contacts" && (
          <div className="space-y-3" data-testid="admin-contacts-list">
            {contacts.length === 0 && <p className="text-cream/50">No contacts</p>}
            {contacts.map((c) => (
              <div key={c.id} className="border border-white/10 p-6">
                <div className="flex justify-between mb-2">
                  <div className="font-display text-[10px] tracking-[0.3em] text-gold">{c.inquiry_type?.toUpperCase()}</div>
                  <div className="text-xs text-cream/50">{new Date(c.created_at).toLocaleString()}</div>
                </div>
                <div className="text-cream font-body mb-1">{c.name} · <span className="text-cream/60">{c.email}</span></div>
                {c.company && <div className="text-cream/60 text-sm font-body">{c.company}</div>}
                <div className="font-editorial italic text-lg text-cream mt-2">{c.subject}</div>
                <div className="text-cream/70 font-body mt-2">{c.message}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "subs" && (
          <div className="grid md:grid-cols-3 gap-3" data-testid="admin-subs-list">
            {subs.length === 0 && <p className="text-cream/50">No subscribers</p>}
            {subs.map((s) => (
              <div key={s.id} className="border border-white/10 p-4 flex justify-between items-center">
                <span className="text-cream text-sm">{s.email}</span>
                <span className="font-display text-[9px] tracking-[0.3em] text-gold">{s.language?.toUpperCase()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
